import { CountryCode, CountryCodes } from '@e3dc-react/shell/Hooks/useCountryOptions'
import { DPP_AVAILABLE_COUNTRY_CODES } from '../const'

const dynamicPowerPriceSoftwareSupportConfig = {
  minimumSupportYear: 2023,
  minimumSupportMonth: 6,
  fullSupportYear: 2024,
  fullSupportMonth: 4,
  fullSupportMonthOnlyGermany: 2,
}

/*
 * This function is used to check if the target has the needed software patch to support dynamic power prices partially (2023_06 or higher) or fully (2024_02 or higher)
 */
export const getSupportedSoftwareVersionsDynamicPowerPrices = (
  softwarePatchVersion?: string
): { doesSupportMinimumVersion: boolean; doesSupportFullVersion: boolean; doesSupportFullVersionOnlyGermany: boolean } => {
  if (softwarePatchVersion === undefined)
    return { doesSupportMinimumVersion: false, doesSupportFullVersion: false, doesSupportFullVersionOnlyGermany: false }

  // eslint-disable-next-line
  const [_, year, month] = softwarePatchVersion.split('_')

  const doesSupportFullVersion =
    (Number(year) === dynamicPowerPriceSoftwareSupportConfig.fullSupportYear &&
      Number(month) >= dynamicPowerPriceSoftwareSupportConfig.fullSupportMonth) ||
    Number(year) > dynamicPowerPriceSoftwareSupportConfig.fullSupportYear

  const doesSupportFullVersionOnlyGermany =
    Number(year) === dynamicPowerPriceSoftwareSupportConfig.fullSupportYear &&
    Number(month) === dynamicPowerPriceSoftwareSupportConfig.fullSupportMonthOnlyGermany

  const doesSupportMinimumVersion =
    (Number(year) === dynamicPowerPriceSoftwareSupportConfig.minimumSupportYear &&
      Number(month) >= dynamicPowerPriceSoftwareSupportConfig.minimumSupportMonth) ||
    (Number(year) > dynamicPowerPriceSoftwareSupportConfig.minimumSupportYear &&
      Number(year) <= dynamicPowerPriceSoftwareSupportConfig.fullSupportYear &&
      Number(month) < dynamicPowerPriceSoftwareSupportConfig.fullSupportMonthOnlyGermany)

  return {
    doesSupportFullVersionOnlyGermany,
    doesSupportMinimumVersion,
    doesSupportFullVersion,
  }
}

export const getIsDppAvailableForInstallationLocation = (countryCode: CountryCode | undefined): boolean => {
  if (!countryCode) return false
  return DPP_AVAILABLE_COUNTRY_CODES.includes(countryCode)
}

/**
 * This function is used to check if the dynamic power prices are available for the current installation
 *  based on supported software versions, location and the availability of dynamic prices settings.
 */
export const getDynamicPowerPricesVersion = (
  doesSupportMinimumVersion: boolean,
  doesSupportFullVersion: boolean,
  doesSupportFullVersionOnlyGermany: boolean,
  isDynamicPricesSettingsAvailable: boolean,
  countryCode?: CountryCodes
): { isMinimumVersionValid: boolean; isFullVersionValid: boolean } => {
  let isMinimumVersionValid = false
  let isFullVersionValid = false

  if (doesSupportMinimumVersion) {
    if (isDynamicPricesSettingsAvailable || countryCode === CountryCodes.GERMANY) {
      isMinimumVersionValid = true
    }
  }

  if (doesSupportFullVersionOnlyGermany) {
    if (isDynamicPricesSettingsAvailable || countryCode === CountryCodes.GERMANY) {
      isFullVersionValid = true
    }
  }

  if (doesSupportFullVersion) {
    if (isDynamicPricesSettingsAvailable || getIsDppAvailableForInstallationLocation(countryCode)) {
      isFullVersionValid = true
    }
  }

  return { isMinimumVersionValid, isFullVersionValid }
}

/**
 * This function format a given value to a string with a fallback value and a number of decimals
 */
export const formatStringToNumberWithFallback = (value: string | number | undefined, decimals?: number, defaultValue = '0'): string => {
  const number = Number(value)
  return isNaN(number) ? defaultValue : number.toFixed(decimals ?? 0)
}
